/**
 * TxHistory — Transaction history display.
 *
 * Features: sorted by time, status badges, token amounts, explorer links, empty state.
 */

import { useState, useEffect, type ReactNode } from 'react';
import { getTxHistory } from '../storage.js';
import {
  shortenAddress,
  explorerTxUrl,
  formatDuration,
  formatTokenAmount,
  getChainConfig,
} from '@tokenstreaming/shared';
import { useWallet } from '../WalletContext.js';
import type { TxRecord } from '../types.js';

const PAGE_SIZE = 20;

export function TxHistory({ onBack }: { onBack: () => void }): ReactNode {
  const { chainId } = useWallet();
  const chain = getChainConfig(chainId);
  const [transactions, setTransactions] = useState<TxRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    void loadTransactions(0);
  }, []);

  const loadTransactions = async (pageNum: number) => {
    setLoading(true);
    const txs = await getTxHistory(PAGE_SIZE + 1, pageNum * PAGE_SIZE);
    setHasMore(txs.length > PAGE_SIZE);
    setTransactions(prev => pageNum === 0 ? txs.slice(0, PAGE_SIZE) : [...prev, ...txs.slice(0, PAGE_SIZE)]);
    setPage(pageNum);
    setLoading(false);
  };

  const handleLoadMore = () => {
    void loadTransactions(page + 1);
  };

  const statusBadge = (status: TxRecord['status']) => {
    const classes: Record<string, string> = {
      pending: 'badge badge-warning',
      confirmed: 'badge badge-success',
      failed: 'badge badge-error',
    };
    return <span className={classes[status] ?? 'badge'}>{status}</span>;
  };

  const typeIcon = (type: TxRecord['type']) => {
    const icons: Record<string, string> = {
      send: '\u2191',
      receive: '\u2193',
      approve: '\u2713',
      'channel-open': '\u25B6',
      'channel-close': '\u25A0',
      reclaim: '\u21A9',
    };
    return icons[type] ?? '\u2022';
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="screen">
        <div className="screen-header">
          <button className="btn-ghost" onClick={onBack}>Back</button>
          <h2>Transaction History</h2>
        </div>
        <div className="screen-body centered">
          <div className="loading-spinner" />
          <p className="text-secondary">Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="btn-ghost" onClick={onBack}>Back</button>
        <h2>Transaction History</h2>
      </div>
      <div className="screen-body">
        {transactions.length === 0 ? (
          <div className="empty-state">
            <p className="text-secondary">No transactions yet</p>
            <p className="text-muted text-sm">
              Send tokens or start a streaming call to see activity here.
            </p>
          </div>
        ) : (
          <div className="tx-list">
            {transactions.map(tx => (
              <a
                key={tx.hash}
                href={explorerTxUrl(chain.explorerUrl, tx.hash)}
                target="_blank"
                rel="noopener noreferrer"
                className="tx-item"
              >
                <div className="tx-icon">{typeIcon(tx.type)}</div>
                <div className="tx-details">
                  <div className="tx-main">
                    <span className="tx-type">{tx.type}</span>
                    {statusBadge(tx.status)}
                  </div>
                  <div className="tx-sub">
                    <span className="tx-address">
                      {tx.type === 'receive' ? `From ${shortenAddress(tx.from)}` : `To ${shortenAddress(tx.to)}`}
                    </span>
                    <span className="tx-time">
                      {formatDuration(Date.now() - tx.timestamp)} ago
                    </span>
                  </div>
                </div>
                <div className="tx-amount">
                  <span className={tx.type === 'receive' ? 'amount-positive' : 'amount-negative'}>
                    {tx.type === 'receive' ? '+' : '-'}{formatTokenAmount(BigInt(tx.amount), tx.tokenDecimals ?? 18)}
                  </span>
                  <span className="tx-token">{tx.tokenSymbol}</span>
                </div>
              </a>
            ))}
          </div>
        )}

        {hasMore && (
          <button className="btn-ghost btn-full" onClick={handleLoadMore} disabled={loading}>
            {loading ? 'Loading...' : 'Load More'}
          </button>
        )}
      </div>
    </div>
  );
}
